import {CdkStep, StepperSelectionEvent} from '@angular/cdk/stepper';
import {Component, Input, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {first, tap} from 'rxjs/operators';
import {FormParts, FormStep} from '../../interfaces/forms/form-element';
import {AbstractControlInt, StepEventLike} from '../../interfaces/step-event-like';
import {FormService} from './../../services/form/form.service';
import {FieldService} from '../../services/field/field.service';
import {DynamicComponentBaseComponent} from '../../../dynamic-components/components/dynamic-component-base/dynamic-component-base.component';

@Component({
    selector: 'app-step',
    templateUrl: './step.component.html',
    styleUrls: ['./step.component.scss'],
    standalone: false
})
export class StepComponent extends DynamicComponentBaseComponent implements OnInit {
  // DEV NOTE: Use this to change the current step
  @Input() currentStep = 0;

  @Input() override data!: {
    formData: FormParts<FormStep>;
    formGroup: FormGroup<Record<string, FormGroup>>;
  };

  isGoingBack = false;
  isComplete = false;

  fg: AbstractControl = new FormControl();

  formGroupSubscriber: Subscription | undefined;

  constructor(
    private formService: FormService,
    private fieldService: FieldService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.data.formGroup = this.generateFirstStep();
    this.isComplete = true;
  }

  generateFirstStep() {
    const form = this.data.formData.form as FormStep[];
    return this.formService.generateStepsFormGroup(this.currentStep, form, this.data.formGroup);
  }

  onSelectionChange(evt: StepperSelectionEvent) {
    const {previouslySelectedIndex, previouslySelectedStep, selectedIndex, selectedStep} = evt;
    const {
      formGroup,
      formData: {form: formStep},
    } = this.data;

    this.formGroupSubscriber?.unsubscribe();
    if (selectedIndex < previouslySelectedIndex) {
      const stepName = this.data.formData.form[selectedIndex].name;
      const stepLength = this.data.formData.form.length;
      const values = this.data.formGroup.get(stepName)?.value as Record<string, any>; // Modifica qui
      const fieldConvertedValues = Object.keys(values).reduce(
        (obj, el) => {
          if (values[el]) {
            obj[el] = this.fieldService.getParamsSync(el, stepName);
            delete obj[el].value;
          }
          return obj;
        },
        {} as Record<string, any>,
      ); // Modifica qui
      this.fieldService.setParamsMultiple(fieldConvertedValues, stepName);
      this.formGroupSubscriber = this.data.formGroup
        .get(stepName)
        ?.valueChanges.pipe(first())
        .subscribe((res) => {
          for (let index = selectedIndex + 1; index < stepLength; index++) {
            const currStepName = this.data.formData.form[index].name;
            this.fieldService.deleteStepParams(currStepName);
            (<AbstractControlInt>this.data.formGroup.get(currStepName)).interacted = false;
          }
          evt.previouslySelectedStep.reset();
        });
      const stepLike: StepEventLike = {
        selectedIndex: evt.selectedIndex,
        previouslySelectedIndex: this.currentStep,
        selectedStep: this.data.formGroup.get(stepName),
        previouslySelectedStep: this.data.formGroup.get(this.data.formData.form[this.currentStep].name),
        changeStep: new Subject(),
      };
      // this.currentStep = evt.selectedIndex;
      this.formService.setFormStepInteract(stepLike);
    }
  }

  onInteracted(evt: CdkStep) {
    this.formGroupSubscriber?.unsubscribe();
  }

  onStepClick(nextStep: number) {
    const stepName = this.data.formData.form[nextStep].name;
    const stepLike: StepEventLike = {
      selectedIndex: nextStep,
      previouslySelectedIndex: this.currentStep,
      selectedStep: this.data.formGroup.get(stepName),
      previouslySelectedStep: this.data.formGroup.get(this.data.formData.form[this.currentStep].name),
      changeStep: new Subject(),
    };
    if (nextStep > this.currentStep) {
      this.formGroupSubscriber?.unsubscribe();
      this.gotoNextStep(stepLike);
      if (stepLike.selectedStep && !stepLike?.selectedStep?.interacted) {
        this.data.formGroup = this.formService.generateStepsFormGroup(stepLike.selectedIndex, this.data.formData.form, this.data.formGroup);
        stepLike.selectedStep.interacted = true;
      }
    }
  }

  gotoNextStep(stepLike: StepEventLike) {
    if (this.formService.isFormStepInteractObserved()) {
      stepLike.changeStep
        .pipe(
          first(),
          tap((res) => {
            if (res) {
              this.currentStep = stepLike.selectedIndex;
              stepLike.selectedStep?.markAsUntouched();
              stepLike.selectedStep?.markAsPristine();
            }
          }),
        )
        .subscribe();
      this.formService.setFormStepInteract(stepLike);
    } else {
      this.currentStep = stepLike.selectedIndex;
    }
  }
}
